import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Copy, Check, Code2, Rocket, Sparkles } from "lucide-react";
import { ASSETS } from "@/data/content";
import { EXPORT_BLOCKS } from "@/data/flatsomeBlocks";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DYNAMIC_BLOCK_META = [
  { key: "css", title: "D1. Page CSS (scoped ss3- styles)", description: "Single-line <style> block powering every dynamic element and hover effect. Paste ONCE at the top of the page in a UX Builder HTML element (single-line format is wpautop-safe)." },
  { key: "scorecard_html", title: "D2. Visibility Scorecard (animated)", description: "The live scorecard card with animated progress bars. Paste into the left column of the dark outcomes section (HTML element)." },
  { key: "machine_html", title: "D3. Machine Diagram (animated)", description: "Engine chips, spinning Automation Core and output row with hover lifts. Paste into the right column of the IMM section." },
  { key: "industries_html", title: "D4. Industry Flavor Cards (hover effects)", description: "Six responsive industry cards with hover lift + red border accent. Paste below the IMM feature row." },
  { key: "stats_html", title: "D5. Count-up Stats", description: "Stats grid whose numbers count up on scroll (887+ / 751 / 98% / years auto-computed from 2006). Paste inside the dark stats section." },
  { key: "js", title: "D6. Page JS (count-up + bar animation)", description: "Single-line <script> that powers the count-up numbers and scorecard bars via IntersectionObserver. Paste ONCE at the very bottom of the page." },
];

const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (e) {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      return true;
    } catch (err) {
      return false;
    }
  }
};

export default function FlatsomeExportPage() {
  const [copiedKey, setCopiedKey] = useState(null);
  const [snippets, setSnippets] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/wp/snippets`)
      .then(({ data }) => setSnippets(data.snippets))
      .catch(() => setSnippets(null));
  }, []);

  const onCopy = async (block) => {
    const ok = await copyText(block.code);
    if (ok) {
      setCopiedKey(block.key);
      toast.success(`Copied: ${block.title}`);
      setTimeout(() => setCopiedKey(null), 2000);
    } else {
      toast.error("Copy failed — select the code and copy manually.");
    }
  };

  return (
    <div data-testid="flatsome-export-page" className="min-h-screen bg-[#fafafa]">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-white border-b border-[#ececec] shadow-[0_4px_20px_rgba(2,2,2,0.04)]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-[68px] flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <div className="inline-flex items-center bg-[#0b0b0c] rounded-[10px] px-3 py-2 shrink-0">
              <img src={ASSETS.logo} alt="Slingshot SEO" className="h-6 w-auto" />
            </div>
            <span className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-[#e9f1fd] border border-[#c8dcf7] px-3 py-1 font-nunito text-[10px] font-extrabold uppercase tracking-[0.14em] text-[#0050c0]">
              <Code2 size={12} strokeWidth={2} />
              Flatsome 3.0 Deployment Kit
            </span>
          </div>
          <Link
            to="/"
            data-testid="flatsome-export-back-link"
            className="inline-flex items-center gap-2 rounded-[10px] border border-[#e0e0e0] px-4 py-2 font-nunito text-[11px] font-extrabold uppercase tracking-[0.12em] text-[#383838] hover:border-[#0050c0] hover:text-[#0050c0] transition-colors duration-150 shrink-0"
          >
            <ArrowLeft size={14} strokeWidth={2} />
            Back to Demo
          </Link>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <h1 className="font-nunito text-2xl sm:text-3xl font-extrabold text-[#383838]">
          Homepage Refresh — Flatsome 3.0 Copy/Paste Blocks
        </h1>
        <p className="mt-3 text-sm text-[#6e6e6e] leading-relaxed max-w-3xl">
          Every section of the demo homepage as UX Builder shortcodes, plus JSON-LD schema and
          Gravity Forms notes. Paste blocks into the UX Builder in the order listed — the layout,
          brand colors and section rhythm match the current live theme.
        </p>

        {/* Deploy Engine banner */}
        <div data-testid="export-deploy-banner" className="mt-6 rounded-[14px] border border-[#c8dcf7] bg-[#e9f1fd]/70 p-5 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <Rocket size={20} strokeWidth={1.75} className="text-[#0050c0] mt-0.5 shrink-0" />
            <div>
              <h2 className="font-nunito text-[15px] font-extrabold text-[#383838]">Skip the copy/paste — the Deploy Engine does all of this automatically</h2>
              <p className="mt-1 text-[13px] text-[#6e6e6e]">Generates the icon PNGs, uploads all media to your library, builds the page with every dynamic effect intact, and saves it as a draft at /homepage2/.</p>
            </div>
          </div>
          <Link
            to="/deploy"
            data-testid="export-deploy-link"
            className="inline-flex items-center gap-2 rounded-[10px] bg-[#0050c0] px-5 py-2.5 font-nunito text-[12px] font-extrabold uppercase tracking-[0.12em] text-white hover:bg-[#0343a0] transition-colors duration-150 shrink-0"
          >
            <Rocket size={14} strokeWidth={2} />
            Open Deploy Engine
          </Link>
        </div>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-[230px,1fr] gap-10">
          {/* TOC */}
          <aside className="hidden lg:block">
            <nav className="sticky top-24 space-y-1" aria-label="Blocks" data-testid="flatsome-export-toc">
              {EXPORT_BLOCKS.map((block) => (
                <a
                  key={block.key}
                  href={`#block-${block.key}`}
                  className="block rounded-[8px] px-3 py-2 text-[13px] text-[#6e6e6e] hover:text-[#0050c0] hover:bg-white transition-colors duration-150"
                >
                  {block.title}
                </a>
              ))}
            </nav>
          </aside>

          {/* Blocks */}
          <div className="space-y-8 min-w-0">
            {EXPORT_BLOCKS.map((block) => (
              <section
                key={block.key}
                id={`block-${block.key}`}
                data-testid={`flatsome-export-block-${block.key}`}
                className="rounded-[14px] bg-white border border-[#ececec] shadow-[0_6px_18px_rgba(2,2,2,0.04)] overflow-hidden scroll-mt-24"
              >
                <div className="flex items-start justify-between gap-4 p-5 sm:p-6 border-b border-[#f1f1f1]">
                  <div>
                    <h2 className="font-nunito text-base sm:text-lg font-extrabold text-[#383838]">
                      {block.title}
                    </h2>
                    <p className="mt-1.5 text-[13px] text-[#6e6e6e] leading-relaxed">{block.description}</p>
                    {block.notes.length > 0 && (
                      <ul className="mt-3 space-y-1">
                        {block.notes.map((note, i) => (
                          <li key={i} className="text-[12px] text-[#8a8a8a] leading-snug pl-4 relative">
                            <span className="absolute left-0 top-[7px] h-1.5 w-1.5 rounded-full bg-[#0050c0]/50" />
                            {note}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => onCopy(block)}
                    data-testid={`flatsome-export-copy-${block.key}-button`}
                    className={`shrink-0 inline-flex items-center gap-2 rounded-[10px] px-4 py-2.5 font-nunito text-[11px] font-extrabold uppercase tracking-[0.12em] transition-colors duration-150 ${
                      copiedKey === block.key
                        ? "bg-[#1f9d55] text-white"
                        : "bg-[#0050c0] text-white hover:bg-[#0343a0]"
                    }`}
                  >
                    {copiedKey === block.key ? <Check size={14} strokeWidth={2.5} /> : <Copy size={14} strokeWidth={2} />}
                    {copiedKey === block.key ? "Copied" : "Copy"}
                  </button>
                </div>
                <pre className="m-0 p-5 sm:p-6 bg-[#0b0b0c] text-[#d7d7d7] text-[12px] leading-relaxed overflow-auto max-h-[420px] whitespace-pre-wrap break-words">
                  {block.code}
                </pre>
              </section>
            ))}

            {/* Dynamic Element Blocks (live from the engine) */}
            <div className="pt-4" id="dynamic-blocks">
              <div className="flex items-center gap-2.5">
                <Sparkles size={18} strokeWidth={1.75} className="text-[#f07000]" />
                <h2 className="font-nunito text-xl font-extrabold text-[#383838]">Dynamic Element Blocks — no screenshots needed</h2>
              </div>
              <p className="mt-2 text-[13px] text-[#6e6e6e] leading-relaxed max-w-3xl">
                The animated Scorecard, Machine diagram, Industry cards and count-up Stats as
                paste-ready HTML/CSS/JS for UX Builder's HTML element. CSS/JS are single-line
                (wpautop-safe). Icon URLs show placeholders here — the Deploy Engine substitutes
                the real uploaded media URLs automatically.
              </p>
            </div>
            {snippets ? (
              DYNAMIC_BLOCK_META.map((meta) => (
                <section
                  key={meta.key}
                  id={`block-dyn-${meta.key}`}
                  data-testid={`flatsome-export-block-dyn-${meta.key}`}
                  className="rounded-[14px] bg-white border border-[#ececec] shadow-[0_6px_18px_rgba(2,2,2,0.04)] overflow-hidden scroll-mt-24"
                >
                  <div className="flex items-start justify-between gap-4 p-5 sm:p-6 border-b border-[#f1f1f1]">
                    <div>
                      <h2 className="font-nunito text-base sm:text-lg font-extrabold text-[#383838]">{meta.title}</h2>
                      <p className="mt-1.5 text-[13px] text-[#6e6e6e] leading-relaxed">{meta.description}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => onCopy({ key: `dyn-${meta.key}`, title: meta.title, code: snippets[meta.key] })}
                      data-testid={`flatsome-export-copy-dyn-${meta.key}-button`}
                      className={`shrink-0 inline-flex items-center gap-2 rounded-[10px] px-4 py-2.5 font-nunito text-[11px] font-extrabold uppercase tracking-[0.12em] transition-colors duration-150 ${
                        copiedKey === `dyn-${meta.key}`
                          ? "bg-[#1f9d55] text-white"
                          : "bg-[#0050c0] text-white hover:bg-[#0343a0]"
                      }`}
                    >
                      {copiedKey === `dyn-${meta.key}` ? <Check size={14} strokeWidth={2.5} /> : <Copy size={14} strokeWidth={2} />}
                      {copiedKey === `dyn-${meta.key}` ? "Copied" : "Copy"}
                    </button>
                  </div>
                  <pre className="m-0 p-5 sm:p-6 bg-[#0b0b0c] text-[#d7d7d7] text-[12px] leading-relaxed overflow-auto max-h-[320px] whitespace-pre-wrap break-words">
                    {snippets[meta.key]}
                  </pre>
                </section>
              ))
            ) : (
              <p className="text-[13px] text-[#8a8a8a]">Loading dynamic element blocks…</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
